import React from "react"
import EventBox from "components/EventBox"
import tickPrimaryMark from "images/tickPrimaryMark.png"
import crossMark from "images/crossMark.png"
import lateMark from "images/lateMark.png"
import moment from "moment"
import { convertIOSDateToHTMLFormatTime, isInTimeWindow } from "config/utils"
import { ShiftStatus } from "config/constants"
import { PreviousShiftEvent } from "@type/workMonitor.types"

interface Props {
	status: ShiftStatus
	events: PreviousShiftEvent[]
	lateCheckThreshold: number
	acceptanceCriteria: number
}

const shiftLabelMap: Record<string, string> = {
	[ShiftStatus.CHECK]: "CHECK CALLS",
	[ShiftStatus.QR]: "SHIFT PATROLS",
	[ShiftStatus.INVENTORY]: "INVENTORY",
}

const getEventBoxConfig = (
	event: PreviousShiftEvent,
	status: ShiftStatus,
	lateCheckThreshold: number,
	acceptanceCriteria: number,
) => {
	const isMissed = event.isEventMissed

	const handlers: Record<string, () => { image: string; variant: "success" | "danger" | "warning" }> = {
		[ShiftStatus.CHECK]: () => {
			const isLate = isInTimeWindow(moment(event.dueTime), moment(event.eventTime), {
				after: lateCheckThreshold,
			})

			if (isLate) return { image: lateMark, variant: "warning" }

			return {
				image: isMissed ? crossMark : tickPrimaryMark,
				variant: isMissed ? "danger" : "success",
			}
		},

		[ShiftStatus.QR]: () => {
			const scanned = event.scannedQREvents?.length || 0
			const missing = event.missingQREvents?.length || 0
			const total = scanned + missing
			const percentage = total > 0 ? (scanned / total) * 100 : 0
			const isSuccess = percentage >= acceptanceCriteria

			return {
				image: isSuccess ? tickPrimaryMark : crossMark,
				variant: isSuccess ? "success" : "danger",
			}
		},

		[ShiftStatus.INVENTORY]: () => ({
			image: isMissed ? crossMark : tickPrimaryMark,
			variant: isMissed ? "danger" : "success",
		}),
	}

	return handlers?.[status]?.()
}

const EventSection = ({ status, events, lateCheckThreshold, acceptanceCriteria }: Props) => {
	if (!events?.length) return null
	const displayLabel = shiftLabelMap[status]
	const onTimeEvents = events.filter(
		event =>
			!event.isEventMissed &&
			isInTimeWindow(moment(event.dueTime), moment(event.eventTime), {
				after: lateCheckThreshold,
			}),
	).length
	const lateSubmittedEvents = events.filter(event =>
		isInTimeWindow(moment(event.dueTime), moment(event.eventTime), {
			after: lateCheckThreshold,
		}),
	).length
	const failedEvents = events.filter(event => event.isEventMissed).length

	return (
		<div>
			<div className="flex items-center justify-between bg-[#B2DDFF] px-4 py-3">
				<div className="text-3 font-medium ">{displayLabel}</div>
				<div className="flex justify-end space-x-6 font-medium">
					<div className="space-x-2">
						<span>TOTAL DUE:</span>
						<span>{events.length}</span>
					</div>

					{status === ShiftStatus.CHECK && (
						<div className="flex justify-end space-x-6">
							<div className="space-x-2">
								<span>ON TIME:</span>
								<span>{onTimeEvents}</span>
							</div>
							<div className="space-x-2">
								<span>LATE SUBMITTED:</span>
								<span>{lateSubmittedEvents}</span>
							</div>
							<div className="space-x-2">
								<span>FAILED:</span>
								<span> {failedEvents}</span>
							</div>
						</div>
					)}
					{status === ShiftStatus.QR && (
						<div className="space-x-2">
							<span>ACCEPTANCE CRITERIA:</span>
							<span>{acceptanceCriteria}% Coverage</span>
						</div>
					)}
					{status === ShiftStatus.INVENTORY && (
						<>
							<div className="space-x-2">
								<span>SUBMITTED:</span>
								<span>{events.filter(e => !e.isEventMissed).length}</span>
							</div>
							<div className="space-x-2">
								<span>FAILED:</span>
								<span> {failedEvents}</span>
							</div>
						</>
					)}
				</div>
			</div>
			<div className="flex flex-wrap gap-2 pt-3 text-md">
				{events.map(event => {
					const { image, variant } = getEventBoxConfig(event, status, lateCheckThreshold, acceptanceCriteria)

					return (
						<EventBox
							key={event._id}
							label={convertIOSDateToHTMLFormatTime(event.dueTime)}
							icon={<img src={image} alt="map" />}
							variant={variant}
						/>
					)
				})}
			</div>
		</div>
	)
}

export default EventSection
